import React from 'react';

import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Card } from 'stile-shared/src/components/2024/common/Card';
import {
  CardGridCard,
  CardGridSection,
} from 'stile-shared/src/components/2024/common/CardGridSection';
import ExpandableLessonList, {
  LessonType,
} from 'stile-shared/src/components/2024/common/ExpandableLessonList';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { LessonTypesSection } from 'stile-shared/src/components/2024/common/LessonTypesSection';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

/**
 * Page-specific styles for inside-stiles-lessons
 */
const style = `
@media (max-width: 768px) {
  .break-on-mobile {
    display: inline;
  }
  .break-on-mobile::before {
    content: "\\A";
    white-space: pre;
  }
}
`;

if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.innerHTML = style;
  document.head.appendChild(styleTag);
}

const InsideStilesLessonsPageLayout = styled(PageLayout)`
  .header-wrapper {
    background-color: var(--white) !important;
    box-shadow: 0px 78px 22px 0px rgba(0, 0, 0, 0), 0px 50px 20px 0px rgba(0, 0, 0, 0),
      0px 28px 17px 0px rgba(0, 0, 0, 0.02), 0px 12px 12px 0px rgba(0, 0, 0, 0.03),
      0px 3px 7px 0px rgba(0, 0, 0, 0.03);
    @media (max-width: 768px) {
      background-color: var(--stile-green-2) !important;
      box-shadow: unset !important;
    }
  }
`;

const HeaderSection = styled.div`
  background-color: var(--stile-green-2);
  padding: 100px 0 100px 0;

  @media (max-width: 768px) {
    padding: 17px 0 60px 0;
  }

  .header-content {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 25px;
    text-align: center;
  }

  h4 {
    color: var(--white);
    margin: 32px auto 0 auto;
    max-width: 550px;

    @media (max-width: 768px) {
      margin: 25px auto 0 auto;
    }
  }

  h1 {
    color: var(--grey-8);
    margin: auto;
  }
`;

const PedagogyImageWrapper = styled.div`
  width: 100px;

  @media (max-width: 768px) {
    width: 80px;
  }

  .gatsby-image-wrapper {
    width: 100%;
    height: auto;
  }

  img {
    object-fit: contain !important;
  }
`;

const LessonFormatSection = styled<typeof Section>(Section)`
  .content {
    padding-top: 100px !important;
  }

  .grid-layout {
    margin-top: 0 !important;
  }

  .cards-container {
    grid-column: span 12;
    padding-bottom: 150px;

    @media (max-width: 768px) {
      padding-bottom: 80px;
    }

    .card {
      padding-top: 35px;
      &:last-child {
        margin-bottom: 0;
      }

      .card-image img {
        border-radius: 16px;
      }

      .description {
        text-align: center;
      }
    }
  }
`;

function InsideStilesLessonsPage() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  return (
    <InsideStilesLessonsPageLayout>
      <HeaderSection>
        <div className="header-content">
          <h1 className="--jumbo">Inside Stile&apos;s lessons</h1>
          <h4>The perfect mix of print, hands-on and digital</h4>
        </div>
      </HeaderSection>

      <LessonFormatSection textColor="--stile-grey-8">
        <Grid className="cards-container">
          <Card
            backgroundColor="--stile-grey-1"
            cardTitle={<span>Digital</span>}
            description={
              <div>
                Stile&apos;s award-winning platform brings science to life through interactive
                lessons, labs, and projects. Simple, intuitive, and built for modern classrooms —
                sparking collaboration, fuelling curiosity, and making feedback effortless.
              </div>
            }
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/digital-large.jpg"
                alt="Digital"
              />
            }
          />

          <Card
            backgroundColor="--stile-grey-1"
            cardTitle={<span>Print</span>}
            description={
              <div>
                Research shows handwriting boosts understanding, recall, and grades. Stile brings
                learning to life with printable lessons, offline collaboration, and vibrant Stile X
                workbooks that spark curiosity and build confident learners.
              </div>
            }
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/print-large.jpg"
                alt="Print"
              />
            }
          />

          <Card
            backgroundColor="--stile-grey-1"
            cardTitle={<span>Hands-on</span>}
            description={
              <div>
                Stile&apos;s lab kits put real materials in students&apos; hands for experiential
                learning. Organised by unit, each of our 71 labs comes with guides, videos, and
                crystal-clear instructions so teachers can skip the prep and dive straight into
                discovery.
              </div>
            }
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/hands-on-small.jpg"
                alt="Hands-on"
              />
            }
          />
        </Grid>
      </LessonFormatSection>

      <LessonTypesSection
        heading="Rich, varied, and grounded in evidence-based practice"
        content={
          <>
            <ExpandableLessonList
              title="Waves"
              titleBanner={
                isMobile ? (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-waves-mobile.jpg"
                    alt="Waves"
                  />
                ) : (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-waves-desktop.jpg"
                    alt="Waves"
                  />
                )
              }
              lessons={[
                {
                  title: 'Pre-test: Waves',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-01.jpg"
                      alt="Pre-test: Waves"
                    />
                  ),
                  lessonType: LessonType.FORMATIVE_ASSESSMENT,
                  previewLink: 'http://stileapp.com/go/waves_lesson_ex_1',
                },
                {
                  title: 'Shining a light on communication',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-02.jpg"
                      alt="Shining a light on communication"
                    />
                  ),
                  lessonType: LessonType.INVESTIGATION,
                  previewLink: 'http://stileapp.com/go/waves_lesson_ex_2',
                },
                {
                  title: 'Micro:bit Lesson 1: Light into sound',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-03.jpg"
                      alt="Micro:bit Lesson 1: Light into sound"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                },
                {
                  title: 'Modelling global communication',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-04.jpg"
                      alt="Modelling global communication"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '1.1 Sound',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-05.jpg"
                      alt="1.1 Sound"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                },
                {
                  title: '1.2 Wave properties',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-06.jpg"
                      alt="1.2 Wave properties"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                  previewLink: 'http://stileapp.com/go/waves_lesson_ex_3',
                },
                {
                  title: '1.1–1.2 Check-in: Sound',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-07.jpg"
                      alt="1.1–1.2 Check-in: Sound"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '1.3 Investigating resonance',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-08.jpg"
                      alt="1.3 Investigating resonance"
                    />
                  ),
                  lessonType: LessonType.INVESTIGATION,
                },
                {
                  title: '2.1 Reflection',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-09.jpg"
                      alt="2.1 Reflection"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.2 Refraction',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-10.jpg"
                      alt="2.2 Refraction"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                },
                {
                  title: '2.3 Updating our models',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-11.jpg"
                      alt="2.3 Updating our models"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '2.4 Brightness',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-12.jpg"
                      alt="2.4 Brightness"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.5 Colour',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-13.jpg"
                      alt="2.5 Colour"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.6 How light interacts with objects',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-14.jpg"
                      alt="2.6 How light interacts with objects"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.1–2.6 Check-in: Light',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-15.jpg"
                      alt="2.1–2.6 Check-in: Light"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '3.1 The electromagnetic spectrum',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-16.jpg"
                      alt="3.1 The electromagnetic spectrum"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '3.2 Radio waves',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-17.jpg"
                      alt="3.2 Radio waves"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '3.3 Digital signals',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-18.jpg"
                      alt="3.3 Digital signals"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '3.1-3.3 Check-in: Waves and digital signals',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-19.jpg"
                      alt="3.1-3.3 Check-in: Waves and digital signals"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '3.4 Electrical circuits and energy transfer',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-20.jpg"
                      alt="3.4 Electrical circuits and energy transfer"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '3.5 Investigating parallel circuits',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-21.jpg"
                      alt="3.5 Investigating parallel circuits"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: 'Micro:bit Lesson 2: Morse code via radio waves',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-22.jpg"
                      alt="Micro:bit Lesson 2: Morse code via radio waves"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                },
                {
                  title: '4.1 Satellite communication',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-23.jpg"
                      alt="4.1 Satellite communication"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '4.2 Long-distance communication',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-24.jpg"
                      alt="4.2 Long-distance communication"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '4.3 Re-modelling global communication',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-25.jpg"
                      alt="4.3 Re-modelling global communication"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '4.4 Bridging the digital divide',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-26.jpg"
                      alt="4.4 Bridging the digital divide"
                    />
                  ),
                  lessonType: LessonType.RESEARCH_PROJECT,
                  previewLink: 'http://stileapp.com/go/waves_lesson_ex_4',
                },
                {
                  title: 'Glossary: Waves',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-27.jpg"
                      alt="Glossary: Waves"
                    />
                  ),
                  lessonType: LessonType.GLOSSARY,
                },
                {
                  title: 'Test: Waves',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Waves-28.jpg"
                      alt="Test: Waves"
                    />
                  ),
                  lessonType: LessonType.TEST,
                },
              ]}
            />

            <ExpandableLessonList
              title={
                <>
                  Classification and <span className="break-on-mobile">Biodiversity</span>
                </>
              }
              titleBanner={
                isMobile ? (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-biodiversity-mobile.jpg"
                    alt="Classification and Biodiversity"
                  />
                ) : (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-biodiversity-desktop.jpg"
                    alt="Classification and Biodiversity"
                  />
                )
              }
              lessons={[
                {
                  title: 'Pre-test: The importance of biodiversity',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-01.jpg"
                      alt="Pre-test: The importance of biodiversity"
                    />
                  ),
                  lessonType: LessonType.FORMATIVE_ASSESSMENT,
                },
                {
                  title: 'Introduction: The mystery of the missing bees',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-02.jpg"
                      alt="Introduction: The mystery of the missing bees"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                  previewLink: 'http://stileapp.com/go/bio_lesson_ex_1',
                },
                {
                  title: '1.1 Biodiversity and bees',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-03.jpg"
                      alt="1.1 Biodiversity and bees"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '1.2 Changes in biodiversity',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-04.jpg"
                      alt="1.2 Changes in biodiversity"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                  previewLink: 'http://stileapp.com/go/bio_lesson_ex_2',
                },
                {
                  title: '1.1–1.2 Check-in',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-05.jpg"
                      alt="1.1–1.2 Check-in"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '2.1 The benefits of bees',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-06.jpg"
                      alt="2.1 The benefits of bees"
                    />
                  ),
                  lessonType: LessonType.RESEARCH_PROJECT,
                },
                {
                  title: '2.2 Making a claim using evidence',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-07.jpg"
                      alt="2.2 Making a claim using evidence"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                  previewLink: 'http://stileapp.com/go/bio_lesson_ex_3',
                },
                {
                  title: '2.3 Take me home!',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-08.jpg"
                      alt="2.3 Take me home!"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '2.4 Human impacts on bees',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-09.jpg"
                      alt="2.4 Human impacts on bees"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.5 Human impacts on ecosystems',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-10.jpg"
                      alt="2.5 Human impacts on ecosystems"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.1–2.5 Check-in',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-11.jpg"
                      alt="2.1–2.5 Check-in"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '3.1 Defining the problem',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-12.jpg"
                      alt="3.1 Defining the problem"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                  previewLink: 'http://stileapp.com/go/bio_lesson_ex_4',
                },
                {
                  title: '3.2 Research and brainstorming',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-13.jpg"
                      alt="3.2 Research and brainstorming"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                },
                {
                  title: '3.3 Designing a solution',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-14.jpg"
                      alt="3.3 Designing a solution"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                },
                {
                  title: '3.4 Improving your solution',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-15.jpg"
                      alt="3.4 Improving your solution"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                },
                {
                  title: '3.5 Sharing your solution',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-16.jpg"
                      alt="3.5 Sharing your solution"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                },
                {
                  title: '3.6 Showcase',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-17.jpg"
                      alt="3.6 Showcase"
                    />
                  ),
                  lessonType: LessonType.ENGINEERING_CHALLENGE,
                },
                {
                  title: 'Glossary: The importance of biodiversity',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-18.jpg"
                      alt="Glossary: The importance of biodiversity"
                    />
                  ),
                  lessonType: LessonType.GLOSSARY,
                },
                {
                  title: 'Test: The importance of biodiversity',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Biodiversity-19.jpg"
                      alt="Test: The importance of biodiversity"
                    />
                  ),
                  lessonType: LessonType.TEST,
                },
              ]}
            />

            <ExpandableLessonList
              title={
                <>
                  Elements and <span className="break-on-mobile">Compounds</span>
                </>
              }
              titleBanner={
                isMobile ? (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-elements-mobile.jpg"
                    alt="Elements and compounds"
                  />
                ) : (
                  <StaticImage
                    src="../../../../stile-shared/assets/images/inside-stiles-lessons/unit-elements-desktop.jpg"
                    alt="Elements and compounds"
                  />
                )
              }
              lessons={[
                {
                  title: 'Introduction: Should we keep mining lithium?',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-01.jpg"
                      alt="Introduction: Should we keep mining lithium?"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: 'Pre-test: Elements and compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-02.jpg"
                      alt="Pre-test: Elements and compounds"
                    />
                  ),
                  lessonType: LessonType.FORMATIVE_ASSESSMENT,
                },
                {
                  title: 'Questions board: Should we keep mining lithium?',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-03.jpg"
                      alt="Questions board: Should we keep mining lithium?"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '1.1 Evolving models of the atom',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-04.jpg"
                      alt="1.1 Evolving models of the atom"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '1.2 The structure of atoms',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-05.jpg"
                      alt="1.2 The structure of atoms"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '1.2 Check-in: The structure of atoms',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-06.jpg"
                      alt="1.2 Check-in: The structure of atoms"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '1.3 Elements',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-07.jpg"
                      alt="1.3 Elements"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '1.4 Modelling lithium mining',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-08.jpg"
                      alt="1.4 Modelling lithium mining"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                  previewLink: 'http://stileapp.com/go/elements_lesson_ex_1',
                },
                {
                  title: '2.1 Organising the elements',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-09.jpg"
                      alt="2.1 Organising the elements"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                },
                {
                  title: '2.2 Reactivity of metals',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-10.jpg"
                      alt="2.2 Reactivity of metals"
                    />
                  ),
                  lessonType: LessonType.PRACTICAL_ACTIVITY,
                },
                {
                  title: '2.3 Replacing lithium',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-11.jpg"
                      alt="2.3 Replacing lithium"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '2.1–2.3 Check-in: The periodic table',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-12.jpg"
                      alt="2.1–2.3 Check-in: The periodic table"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                  previewLink: 'http://stileapp.com/go/elements_lesson_ex_2',
                },
                {
                  title: '2.4 The forbidden element',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-13.jpg"
                      alt="2.4 The forbidden element"
                    />
                  ),
                  lessonType: LessonType.HANDS_ON_ACTIVITY,
                  previewLink: 'http://stileapp.com/go/elements_lesson_ex_3',
                },
                {
                  title: '3.1 Compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-14.jpg"
                      alt="3.1 Compounds"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '3.1 Check-in: Compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-15.jpg"
                      alt="3.1 Check-in: Compounds"
                    />
                  ),
                  lessonType: LessonType.CHECK_IN,
                },
                {
                  title: '3.2 Modelling compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-16.jpg"
                      alt="3.2 Modelling compounds"
                    />
                  ),
                  lessonType: LessonType.CLASSROOM_LESSON,
                },
                {
                  title: '4.1 Research: The future of lithium mining',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-17.jpg"
                      alt="4.1 Research: The future of lithium mining"
                    />
                  ),
                  lessonType: LessonType.RESEARCH_PROJECT,
                },
                {
                  title: '4.2 Discuss: The future of lithium mining',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-18.jpg"
                      alt="4.2 Discuss: The future of lithium mining"
                    />
                  ),
                  lessonType: LessonType.RESEARCH_PROJECT,
                },
                {
                  title: 'Glossary: Elements and compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-19.jpg"
                      alt="Glossary: Elements and compounds"
                    />
                  ),
                  lessonType: LessonType.GLOSSARY,
                },
                {
                  title: 'Test: Elements and compounds',
                  image: (
                    <StaticImage
                      src="../../../../stile-shared/assets/images/inside-stiles-lessons/Elements-20.jpg"
                      alt="Test: Elements and compounds"
                    />
                  ),
                  lessonType: LessonType.TEST,
                  previewLink: 'http://stileapp.com/go/elements_lesson_ex_4',
                },
              ]}
            />
          </>
        }
      />

      <CardGridSection
        heading={<h2>Underpinned pedagogies</h2>}
        headingButton={
          <Button
            newTab={true}
            href="/pedagogies/5e-instructional-model/"
            label={<span>Explore all pedagogies</span>}
          />
        }
        columns={3}
      >
        <CardGridCard
          image={
            <PedagogyImageWrapper>
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/pedagogy-5e.png"
                alt="5E instructional model"
              />
            </PedagogyImageWrapper>
          }
          title="5E instructional model"
          subtitle="Evidence-based model that's nurtures curiosity and consolidates learning."
          link="/pedagogies/5e-instructional-model/"
        />
        <CardGridCard
          image={
            <PedagogyImageWrapper>
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/pedagogy-solo.png"
                alt="SOLO taxonomy"
              />
            </PedagogyImageWrapper>
          }
          title="SOLO taxonomy"
          subtitle="Scaffolded developmental progression through to extended abstract thinking."
          link="/pedagogies/solo-taxonomy/"
        />
        <CardGridCard
          image={
            <PedagogyImageWrapper>
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/pedagogy-explicit-instruction.png"
                alt="Explicit instruction"
              />
            </PedagogyImageWrapper>
          }
          title="Explicit instruction"
          subtitle="Explain, demonstrate and model the learning process."
          link="/pedagogies/explicit-instruction/"
        />
        <CardGridCard
          image={
            <PedagogyImageWrapper>
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/pedagogy-cognitive-load-theory.png"
                alt="Cognitive load theory"
              />
            </PedagogyImageWrapper>
          }
          title="Cognitive load theory"
          subtitle="Students use their mental focus on learning and memory."
          link="/pedagogies/cognitive-load-theory/"
        />
        <CardGridCard
          image={
            <PedagogyImageWrapper>
              <StaticImage
                src="../../../../stile-shared/assets/images/inside-stiles-lessons/pedagogy-differentiated-instruction.png"
                alt="Differentiated instruction"
              />
            </PedagogyImageWrapper>
          }
          title="Differentiated instruction"
          subtitle="Meet students where they are."
          link="/pedagogies/differentiated-instruction/"
        />
      </CardGridSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Stile X Workbooks',
            description:
              'Designed for hands-on learning and perfectly matched to the digital curriculum.',
            url: '/what-is-stile/stile-x/',
          },
          {
            title: 'Professional Learning',
            description:
              'Master Stile at your own pace with evidence-based pedagogies and personalised support.',
            url: '/what-is-stile/professional-learning/',
          },
        ]}
      />
    </InsideStilesLessonsPageLayout>
  );
}

export default InsideStilesLessonsPage;
